import React from "react";
import Loadable from "react-loadable";
import styled from "styled-components";
import { RouteComponentProps, Route, Switch } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";

import { ROUTES } from "@config/app.routing";
import { APPLY } from "@config/testIds/apply";

import { AVAILABLE_COUNTRIES } from "@typings/globals";

import { Container, Row, Column } from "@ui/Atoms/Grid";
import { Section, OptionPanel, FadeInContainer } from "@ui/Atoms";
import { StepProgress } from "@ui/Molecules";
import {
  ApplyFormFirstPageSkeleton,
  ApplyFormShortPageSkeleton,
  ApplyFormLongPageSkeleton,
} from "@ui/Skeletons/Skeletons";

import { TokenGuard } from "@containers/TokenGuard";

import { useTranslate } from "@hooks/useTranslate";
import { useLocalisation } from "@hooks/useLocalisation";

import { NotFound } from "@app/pages/NotFound";
import {
  getTokenStatus,
  getAccessToken,
  getLocalStorageStatus,
} from "@app/redux/apply/apply.selectors";
import { deleteAccessToken } from "@app/redux/apply/apply.actions";

import { LocalStorageHandler } from "./components";

const LoadableApplyStart = Loadable({
  loader: () => import("./steps/ApplyStart"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.ApplyStart;
    return <Component {...props} />;
  },
  loading: ApplyFormFirstPageSkeleton,
});

const LoadablePersonalInformation = Loadable({
  loader: () => import("./steps/PersonalInformation"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.PersonalInformation;
    return <Component {...props} />;
  },
  loading: ApplyFormShortPageSkeleton,
});

const LoadableExperienceInformation = Loadable({
  loader: () => import("./steps/ExperienceInformation"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.ExperienceInformation;
    return <Component {...props} />;
  },
  loading: ApplyFormLongPageSkeleton,
});

const LoadableThankYouPage = Loadable({
  loader: () => import("./steps/ThankYouPage"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.ThankYouPage;
    return <Component {...props} />;
  },
  loading: ApplyFormShortPageSkeleton,
});

const routeToStepMap: { [k: string]: number } = {
  [ROUTES.APPLY]: 1,
  [ROUTES.APPLY_PERSONAL_INFO]: 2,
  [ROUTES.APPLY_EXPERIENCE_INFO]: 3,
};

const Apply = (props: RouteComponentProps): React.ReactElement => {
  const translate = useTranslate("batmaid_pwa_apply");
  const { country } = useLocalisation();

  const dispatch = useDispatch();

  const accessToken = useSelector(getAccessToken);
  const isTokenInvalid = useSelector(getTokenStatus);
  const isLocalStorageChecked = useSelector(getLocalStorageStatus);

  const tokenGuardProps = {
    originRoute: ROUTES.APPLY,
    invalidSessionTokenErrorText: translate("invalid_session_token_error"),
    accessToken,
    isTokenInvalid,
    isLocalStorageChecked,
    onInvalidAccessToken: () => dispatch(deleteAccessToken()),
  };

  return (
    <StyledContainer>
      {country === AVAILABLE_COUNTRIES.CH && (
        <Helmet>
          <title>{translate("meta_title")}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
      <Row>
        <Column>
          {props.location.pathname !== ROUTES.THANK_YOU_PAGE && (
            <Section flat smallMargins>
              <OptionPanel isThin testId={APPLY.STEP_PROGRESS_BAR}>
                <Row>
                  <Column lg={8} pushLg={2}>
                    <StepProgress
                      activeStep={routeToStepMap[props.location.pathname]}
                      steps={[
                        {
                          label: translate("step_start"),
                          route: ROUTES.APPLY,
                          allowDirectNavigation: true,
                        },
                        {
                          label: translate("step_personal_information"),
                          route: ROUTES.APPLY_PERSONAL_INFO,
                        },
                        {
                          label: translate("step_experience_information"),
                          route: ROUTES.APPLY_EXPERIENCE_INFO,
                        },
                      ]}
                    />
                  </Column>
                </Row>
              </OptionPanel>
            </Section>
          )}

          <Section small>
            <Switch>
              {/** 1st Step - Start */}
              <Route
                exact
                path={ROUTES.APPLY}
                render={() => (
                  <FadeInContainer>
                    <LoadableApplyStart />
                  </FadeInContainer>
                )}
              />

              {/** 2nd Step - Personal Information */}
              <Route
                exact
                path={ROUTES.APPLY_PERSONAL_INFO}
                render={() => (
                  <TokenGuard {...tokenGuardProps}>
                    <FadeInContainer>
                      <LoadablePersonalInformation />
                    </FadeInContainer>
                  </TokenGuard>
                )}
              />

              {/** 3rd Step - Experience Information */}
              <Route
                exact
                path={ROUTES.APPLY_EXPERIENCE_INFO}
                render={() => (
                  <TokenGuard {...tokenGuardProps}>
                    <FadeInContainer>
                      <LoadableExperienceInformation translate={translate} />
                    </FadeInContainer>
                  </TokenGuard>
                )}
              />

              {/** Thank you Page */}
              <Route
                exact
                path={ROUTES.THANK_YOU_PAGE}
                render={() => (
                  <TokenGuard {...tokenGuardProps}>
                    <FadeInContainer>
                      <LoadableThankYouPage />
                    </FadeInContainer>
                  </TokenGuard>
                )}
              />

              {/** 404 Page */}
              <Route render={() => <NotFound isNested />} />
            </Switch>
          </Section>
        </Column>
      </Row>
      <LocalStorageHandler />
    </StyledContainer>
  );
};

export { Apply };

const StyledContainer = styled(Container)`
  ${props => props.theme.breakpoints.desktop} {
    padding-top: ${props => props.theme.margins.base_x4};
    padding-bottom: ${props => props.theme.margins.base_x4};
  }
`;
